<template>
    <div>
        <!-- Navigation Bar -->
        <Disclosure as="nav" class="bg-gray-800" v-slot="{ open }">
            <div class="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
                <div class="relative flex h-16 items-center justify-between">
                    <MobileMenuButton :open="open" />
                    <Link :href="route('dashboard')">
                        <LogoUndip class="block h-12 w-12" />
                    </Link>
                    <div class="hidden sm:ml-6 sm:block" v-for="menus in menu" :key="menus.label || menus.id">
                        <NavigationLinks :navigation="menus" />
                    </div>
                    <div class="flex items-center space-x-4">
                        <!-- Profile Dropdown -->
                    <ProfileDropdown :user="user" :profileMenu="userNavigation" />

                        <!-- Tumbas SSO Button - Show only when JWT_SECRET and NUXT_APP_URL are configured -->
                        <button
                            v-if="showTumbasSSOButton"
                            @click="confirmTumbasSSO"
                            class="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 transition-colors duration-200"
                            title="Login ke Tumbas-BO Baru"
                        >
                            <svg class="-ml-0.5 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd" />
                            </svg>
                            <span class="hidden sm:inline">Login ke Tumbas-BO Baru</span>
                        </button>

                        <!-- Logout Button - Rightmost Position -->
                        <button
                            @click="logout"
                            class="inline-flex items-center gap-x-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 transition-colors duration-200"
                            title="Logout"
                        >
                            <svg class="-ml-0.5 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z" clip-rule="evenodd" />
                                <path fill-rule="evenodd" d="M6 10a.75.75 0 01.75-.75h9.546l-1.048-.943a.75.75 0 111.004-1.114l2.5 2.25a.75.75 0 010 1.114l-2.5 2.25a.75.75 0 11-1.004-1.114l1.048-.943H6.75A.75.75 0 016 10z" clip-rule="evenodd" />
                            </svg>
                            <span class="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </div>
            </div>
            <DisclosurePanel class="sm:hidden">
                <div class="space-y-1 px-2 pb-3 pt-2">
                    <template v-for="item in menu" :key="item.label">
                            <a v-if="item.to" :href="route(item.to)" :class="[item.current ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white', 'block rounded-md px-3 py-2 text-base font-medium']" :aria-current="item.current ? 'page' : undefined">{{ item.label }}</a>
                            <div v-else>
                                <DisclosureButton :class="[item.current ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white', 'block rounded-md px-3 py-2 text-base font-medium']" :aria-current="item.current ? 'page' : undefined">{{ item.label }}</DisclosureButton>
                            </div>
                            <div class="ml-4 space-y-1">
                                <a v-for="subItem in item.items" :key="subItem.label" :href="route(subItem.to)" :class="[subItem.current ? 'bg-gray-900 text-gray-50' : 'text-gray-300 hover:bg-gray-700 hover:text-gray-50', 'block rounded-md px-3 py-2 text-base font-medium']" :aria-current="subItem.current ? 'page' : undefined">{{ subItem.label }}</a>
                            </div>
                    </template>
                    <!-- Mobile Tumbas SSO Button -->
                    <button
                        v-if="showTumbasSSOButton"
                        @click="confirmTumbasSSO"
                        class="w-full text-left block rounded-md bg-green-600 px-3 py-2 text-base font-medium text-white hover:bg-green-500 transition-colors duration-200"
                    >
                        <svg class="inline-block mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd" />
                        </svg>
                        Login ke Tumbas-BO Baru
                    </button>
                    <!-- Mobile Logout Button -->
                    <button
                        @click="logout"
                        class="w-full text-left block rounded-md bg-red-600 px-3 py-2 text-base font-medium text-white hover:bg-red-500 transition-colors duration-200"
                    >
                        <svg class="inline-block mr-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z" clip-rule="evenodd" />
                            <path fill-rule="evenodd" d="M6 10a.75.75 0 01.75-.75h9.546l-1.048-.943a.75.75 0 111.004-1.114l2.5 2.25a.75.75 0 010 1.114l-2.5 2.25a.75.75 0 11-1.004-1.114l1.048-.943H6.75A.75.75 0 016 10z" clip-rule="evenodd" />
                        </svg>
                        Logout
                    </button>
                </div>
            </DisclosurePanel>
        </Disclosure>
        <!-- Content -->
        <div :class="containerClass" @click="onWrapperClick">
            <div class="layout-main-container">
                <div class="layout-main">
                    <slot />
                </div>
            </div>
        </div>
    </div>
    <ConfirmDialog></ConfirmDialog>
    <Toast></Toast>
</template>

<script>
import LogoUndip from "@/Logo/LogoUndip.vue"
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { Link } from "@inertiajs/inertia-vue3"
import axios from 'axios'
import ConfirmDialog from 'primevue/confirmdialog'
import Toast from 'primevue/toast'
import MobileMenuButton from '../Components/MobileMenuButton.vue'
import NavigationLinks from '../Components/NavigationLinks.vue'
import ProfileDropdown from '../Components/ProfileDropdown.vue'

export default {
    name: 'AppLayout',
    components: {
        Link,
        MobileMenuButton,
        NavigationLinks,
        ProfileDropdown,
        LogoUndip,
        Disclosure,
        DisclosureButton,
        DisclosurePanel,
        Toast,
        ConfirmDialog
    },
    props: {
        title: String,
        user: {
            type: Object,
            required: true,
            default: () => ({
                avatar: '',
                name: ''
            })
        },
        userNavigation: {
            type: Array,
            required: true,
            default: () => [
                { name: 'Your profile', href: 'profile.show' },
                { name: 'Sign out', href: 'logout' }
            ]
        }
    },
    data() {
        return {
            layoutMode: 'static',
            layoutColorMode: 'light',
            staticMenuInactive: false,
            overlayMenuActive: false,
            mobileMenuActive: false,
            menuClick: false,
            menu: []
        }
    },
    computed: {
        containerClass() {
            return ['layout-wrapper', {
                'layout-overlay': this.layoutMode === 'overlay',
                'layout-static': this.layoutMode === 'static',
                'layout-static-sidebar-inactive': this.staticMenuInactive && this.layoutMode === 'static',
                'layout-overlay-sidebar-active': this.overlayMenuActive && this.layoutMode === 'overlay',
                'layout-mobile-sidebar-active': this.mobileMenuActive,
                'p-input-filled': this.$primevue.config.inputStyle === 'filled',
                'p-ripple-disabled': this.$primevue.config.ripple === false,
            }];
        },
        showTumbasSSOButton() {
            if (!this.$page.props.tumbas_sso) {
                return false;
            }

            const jwtConfigured = this.$page.props.tumbas_sso.jwt_secret_configured;
            const nuxtConfigured = this.$page.props.tumbas_sso.nuxt_app_url_configured;

            return jwtConfigured && nuxtConfigured;
        }
    },
    methods: {
        onWrapperClick() {
            if (!this.menuClick) {
                this.overlayMenuActive = false;
                this.mobileMenuActive = false;
            }
            this.menuClick = false;
        },
        async list_all_side_bar_menu() {
            try {
                const response = await axios.get(route('menus.get_menu_by_role'));
                this.menu = response.data;
                console.log("Menu data:", this.menu);
            } catch (error) {
                console.error(error);
            }
        },
        async logout() {
            try {
                await axios.post(route('logout'));
                window.location.href = '/';
            } catch (error) {
                console.error('Logout failed:', error);
                // Fallback to page reload if logout fails
                window.location.href = '/';
            }
        },
        confirmTumbasSSO() {
            this.$confirm.require({
                message: 'Apakah Anda yakin ingin login ke Tumbas-BO Baru? Anda akan diarahkan ke aplikasi Tumbas yang baru.',
                header: 'Konfirmasi Login SSO',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Ya, Login',
                rejectLabel: 'Batal',
                accept: () => {
                    // Redirect to Tumbas SSO
                    window.location.href = route('tumbas-sso.redirect');
                },
                reject: () => {
                    // Do nothing, user cancelled
                }
            });
        }
    },
    mounted() {
        this.list_all_side_bar_menu();
        console.log("Menu data:", this.menu);
    },
    created() {
        // add user.name to the user object
        this.user.name = this.$page.props.user.username;
        //avatar profile_photo_url
        this.user.avatar = this.$page.props.user.profile_photo_url;
    }
}
</script>
